import { Link } from 'react-router-dom'
import PageHeader from '../components/PageHeader.jsx'
import StatusBadge from '../components/StatusBadge.jsx'
import { useAuth } from '../components/AuthProvider.jsx'
import { useInventory } from '../components/InventoryProvider.jsx'
import { formatDateTime } from '../utils/format'
import { canManageParts } from '../utils/permissions'
import {
  formatCurrentStock,
  formatSignedQty,
  getStockStatus,
  partTitle,
  signedTransactionQty,
} from '../utils/stock'

export default function DashboardPage() {
  const { user } = useAuth()
  const manageParts = canManageParts(user)
  const { summary, parts, loading, error } = useInventory()
  const stats = summary || {
    partCount: 0,
    totalQuantity: 0,
    todayInQty: 0,
    todayOutQty: 0,
    todayInCount: 0,
    todayOutCount: 0,
    recentTransactions: [],
  }
  const recent = (stats.recentTransactions || []).slice(0, 8)
  const unverifiedParts = parts.filter((part) => getStockStatus(part) === 'unverified')
  const lowParts = parts.filter((part) => getStockStatus(part) === 'low')
  const outParts = parts.filter((part) => getStockStatus(part) === 'out')

  return (
    <div className="page">
      <PageHeader title="대시보드" description="공장 재고 현황과 오늘 입출고를 한눈에 확인합니다." />

      {error ? <p className="form-error">{error}</p> : null}
      {loading && !summary ? <p className="muted">데이터를 불러오는 중...</p> : null}

      <section className="stat-grid" aria-label="주요 지표">
        <article className="stat-card">
          <span>등록 부품 종류</span>
          <strong>{stats.partCount || parts.length}</strong>
          <em>종</em>
        </article>
        <article className="stat-card">
          <span>총 재고수량</span>
          <strong>{stats.totalQuantity}</strong>
          <em>확정 수량 합계</em>
        </article>
        <article className="stat-card stat-card--warn">
          <span>재고부족 품목</span>
          <strong>{lowParts.length}</strong>
          <em>확정 수량 기준 · 없음 {outParts.length}</em>
        </article>
        <article className="stat-card stat-card--pending">
          <span>수량 미확인 품목</span>
          <strong>{unverifiedParts.length}</strong>
          <em>종</em>
        </article>
        <article className="stat-card stat-card--in">
          <span>오늘 입고</span>
          <strong>{stats.todayInQty}</strong>
          <em>{stats.todayInCount}건</em>
        </article>
        <article className="stat-card stat-card--out">
          <span>오늘 출고</span>
          <strong>{stats.todayOutQty}</strong>
          <em>{stats.todayOutCount}건</em>
        </article>
      </section>

      <div className="split-grid">
        <section className="panel panel--feed">
          <div className="panel__head">
            <h2>재고 부족 품목</h2>
            <Link to="/inventory?stock=low">재고 목록</Link>
          </div>
          <div className="panel__body">
            {lowParts.length === 0 ? (
              <p className="empty empty--compact">재고 부족 품목이 없습니다.</p>
            ) : (
              <ul className="plain-list plain-list--compact">
                {lowParts.map((part) => (
                  <li key={part.id}>
                    <Link to={`/inventory/${part.id}`} className="plain-row">
                      <div>
                        <strong className="model-name">{partTitle(part)}</strong>
                        <span>
                          {part.manufacturer} · {part.partCode}
                        </span>
                      </div>
                      <div className="plain-row__meta">
                        <b>{formatCurrentStock(part)}</b>
                        <StatusBadge part={part} />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="panel panel--feed">
          <div className="panel__head">
            <h2>수량 미확인 품목</h2>
            <Link to={manageParts ? '/inventory/review?filter=qty' : '/inventory?verification=unverified'}>
              {manageParts ? '재고 검수' : '재고 목록'}
            </Link>
          </div>
          <div className="panel__body">
            {unverifiedParts.length === 0 ? (
              <p className="empty empty--compact">수량 미확인 품목이 없습니다.</p>
            ) : (
              <ul className="plain-list plain-list--compact">
                {unverifiedParts.map((part) => (
                  <li key={part.id}>
                    <Link to={`/inventory/${part.id}`} className="plain-row">
                      <div>
                        <strong className="model-name">{partTitle(part)}</strong>
                        <span>
                          {part.manufacturer} · {part.partCode}
                        </span>
                      </div>
                      <div className="plain-row__meta">
                        <b>미확인</b>
                        <StatusBadge part={part} />
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="panel__head">
          <h2>최근 입출고 내역</h2>
          <Link to="/transactions">전체 보기</Link>
        </div>
        {recent.length === 0 ? (
          <p className="empty">아직 입출고 내역이 없습니다.</p>
        ) : (
          <ul className="plain-list">
            {recent.map((tx) => {
              const delta = signedTransactionQty(tx)
              return (
                <li key={tx.id}>
                  <Link to={`/inventory/${tx.partId}`} className="plain-row">
                    <div>
                      <strong className="model-name">{partTitle(tx)}</strong>
                      <span>
                        {formatDateTime(tx.createdAt)} · {tx.worker || tx.createdBy?.name || ''}
                      </span>
                    </div>
                    <div className="plain-row__meta">
                      <b className={delta < 0 ? 'tx-delta--minus' : 'tx-delta--plus'}>
                        {formatSignedQty(delta)}
                      </b>
                      <StatusBadge kind="tx" status={tx.type} />
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}
